import { Trans } from "@lingui/react/macro";
import { Divider, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import Block from "@/components/Block";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import BookingStepLayout from "@/features/booking/components/BookingStepLayout";
import { usePrices } from "@/hooks/usePrices";
import type { Extra } from "@/types/api";
import { analytics } from "@/utils/analytics";
import { getMandatoryExtrasFromPrice } from "@/utils/extras";
import ExtraItem from "./components/ExtraItem";
import MandatoryExtrasInfoBox from "./components/MandatoryExtrasInfoBox";

export default function Extras() {
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();
  const { prices } = usePrices();
  const price = state.price;

  const handleNext = async () => {
    analytics.capture("extras_submitted", {
      extras_count: state.selectedExtras.length,
      extra_names: state.selectedExtras.map(
        (e) => campgroundData.campground.extras.find((ex) => ex.id === e.id)!.name,
      ),
      price_total: state.price?.total,
      currency: state.price?.currency,
    });
    return true;
  };

  const mandatoryExtras = useMemo(() => {
    return getMandatoryExtrasFromPrice(price!);
  }, [price]);

  const handleExtraToggle = (extra: Extra) => {
    const existing = state.selectedExtras.find((e) => e.id === extra.id);
    if (existing) {
      actions.removeExtra(extra.id);
    } else {
      actions.setExtra({ id: extra.id, amount: 1 });
    }
  };

  const handleExtraAmountChange = (extra: Extra, amount: number) => {
    if (amount === 0) {
      actions.removeExtra(extra.id);
    } else {
      actions.setExtra({ id: extra.id, amount });
    }
  };

  const getExtraAmount = (extraId: number): number => {
    const selected = state.selectedExtras.find((e) => e.id === extraId);
    return selected ? selected.amount : 0;
  };

  const extraPrices = prices?.campground.extras || [];

  return (
    <BookingStepLayout nextAction={handleNext}>
      <Block flushOnMobile>
        <Typography variant="h3" sx={{ display: { xs: "none", md: "block" }, mb: 2 }}>
          <Trans>Extras</Trans>
        </Typography>
        <Stack spacing={2}>
          {extraPrices.length > 0 && (
            <Stack spacing={2} divider={<Divider />}>
              {extraPrices.map((extraPrice) => {
                // get base extra object
                const extra = campgroundData.campground?.extras.find((e) => e.id === extraPrice.id);
                // extract price for the extra for the selected campsite
                const price =
                  extraPrice.prices_per_campsite.find(
                    (p) => p.campsite === state.selectedCampsite?.id,
                  )?.period_price ?? null;
                // extract price in the alternate currency,
                // sum prices for all periods, since there is no price per period in the alternate currency
                const priceAlt =
                  prices?.currency_alt?.campground.extras
                    .find((e) => e.id === extraPrice.id)
                    ?.prices?.find((p) => p.campsite === state.selectedCampsite?.id)
                    ?.prices.reduce((acc, p) => acc + p.price * p.duration, 0) ?? null;
                return (
                  <ExtraItem
                    key={extraPrice.id}
                    extra={extra!}
                    price={price}
                    priceAlt={priceAlt}
                    selectedAmount={getExtraAmount(extra!.id)}
                    onToggle={handleExtraToggle}
                    onAmountChange={handleExtraAmountChange}
                    currency={prices?.currency || campgroundData.campground.currency}
                    currencyAlt={prices?.currency_alt?.currency}
                  />
                );
              })}
            </Stack>
          )}
          {mandatoryExtras.length > 0 && (
            <MandatoryExtrasInfoBox
              extras={mandatoryExtras}
              currency={price!.currency}
              currencyAlt={price?.currency_alt?.currency}
            />
          )}
        </Stack>
      </Block>
    </BookingStepLayout>
  );
}
